import { ReactNode, createContext, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { BOOKING_TYPE } from "../constants/variables";
import { checkForInternationalFlights } from "../services/flightsDetails/checkForInternationalFlights";
import { Contact } from "../global";

export interface BookingDataContextProps {
  children: ReactNode;
}

export const BookingDataContext = createContext<any>({});

function BookingDataContextProvider({ children }: BookingDataContextProps) {
  const router = useRouter();
  const route = router.asPath;
  const [origin, setOrigin] = useState();
  const [destination, setDestination] = useState();
  const [departureDate, setDepartureDate] = useState(new Date());
  const [arrivalDate, setArrivalDate] = useState(null);
  const [departureTimeFilter, setDepartureTimeFilter] = useState({});
  const [passenger, setPassenger] = useState({
    child: 0,
    adult: route.includes("/group-booking") ? 10 : 1,
    seniorcitizen: 0,
    infant: 0,
    extraSeat: 0,
    total: route.includes("/group-booking") ? 10 : 1
  });
  const [journeyKey, setJourneyKey] = useState(null);
  const [fareAvailabilityKey, setFareAvailabilityKey] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [passengerList, setPassengerList] = useState(null);
  const [primaryContact, setPrimaryContact] = useState<Contact | null | undefined>(null);
  const [fareAmount, setFareAmount] = useState(null);
  const [showFooter, setShowFooter] = useState(false);
  const [passengerFare, setPassengerFare] = useState(null);
  const [premiumType, setPremiumType] = useState("saverFare");
  const [flightDetails, setFlightDetails] = useState(null);
  const [feeCodes, setFeeCodes] = useState(null);
  const [passengerFareOfJourney, setPassengerFareOfJourney] = useState(null);
  const [finalViewSummaryBreakup, setFinalViewSummaryBreakup] = useState({});
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeNeedToValidate, setPromoCodeNeedToValidate] = useState("");
  const [preFilledPromoCode,setPreFilledPromoCode] = useState("");
  const [promoCodeStatus, setPromoCodeStatus] = useState(false);
  const [changeBookingOldDate, setChangeBookingOldDate] = useState({});
  const [currentBookingInStateForRebooking, setCurrentBookingInStateForRebooking] = useState({});
  const [tripType, setTripType] = useState(BOOKING_TYPE.ONE_WAY);
  const [tripType2, setTripType2] = useState(BOOKING_TYPE.ONE_WAY);
  const [isInternationalFlight, setIsInternationalFlight] = useState(false);
  const [loadSubmitButton, setLoadSubmitButton] = useState(false);
  const [promoCodeNudgeText, setPromoCodeNudgeText] = useState("");
  const [isDepartureDatePickerOpen, setIsDepartureDatePickerOpen] = useState(false);
  const [isReturnDatePickerOpen, setIsReturnDatePickerOpen] = useState(false);
  const [paxTypesCount, setPaxTypesCount] = useState({
    ADT: {
      value: passenger?.adult,
      isIncDisable: false,
      isDescDisable: true
    },
    CHD: {
      value: passenger?.child,
      isIncDisable: false,
      isDescDisable: true
    },
    SNR: {
      value: passenger?.seniorcitizen,
      isIncDisable: false,
      isDescDisable: true
    },
    INF: {
      value: passenger?.infant,
      isIncDisable: false,
      isDescDisable: true
    },
    EXST: {
      value: passenger?.extraSeat,
      isIncDisable: false,
      isDescDisable: true
    }
  });

  useEffect(() => {
    if (origin && destination) {
      setIsInternationalFlight(checkForInternationalFlights({ origin, destination }));
    }
  }, [origin, destination]);

  useEffect(() => {
    if (tripType === BOOKING_TYPE.ONE_WAY && arrivalDate) {
      setArrivalDate(null);
    }
  }, [tripType]);
  const values = {
    tripType,
    setTripType,
    tripType2,
    setTripType2,
    origin,
    destination,
    departureDate,
    passenger,
    journeyKey,
    fareAvailabilityKey,
    passengerList,
    primaryContact,
    fareAmount,
    showFooter,
    premiumType,
    passengerFare,
    feeCodes,
    promoCode,
    promoCodeNeedToValidate,
    setPromoCodeNeedToValidate,
    promoCodeStatus,
    setPromoCodeStatus,
    setFeeCodes,
    flightDetails,
    arrivalDate,
    setFlightDetails,
    setShowFooter,
    setFareAmount,
    breakdown,
    setPassengerList,
    setPrimaryContact,
    setOrigin,
    setDestination,
    setDepartureDate,
    setPassenger,
    setJourneyKey,
    setFareAvailabilityKey,
    setPremiumType,
    setBreakdown,
    setPassengerFare,
    passengerFareOfJourney,
    setPassengerFareOfJourney,
    finalViewSummaryBreakup,
    setFinalViewSummaryBreakup,
    setArrivalDate,
    setPromoCode,
    currentBookingInStateForRebooking,
    setCurrentBookingInStateForRebooking,
    paxTypesCount, // for retaining the enable and disable buttons in pax details popup
    setPaxTypesCount, // for retaining the enable and disable buttons in pax details popup
    changeBookingOldDate,
    setChangeBookingOldDate,
    isInternationalFlight,
    loadSubmitButton,
    setLoadSubmitButton,
    departureTimeFilter,
    setDepartureTimeFilter,
    preFilledPromoCode,
    setPreFilledPromoCode,
    isDepartureDatePickerOpen,
    setIsDepartureDatePickerOpen,
    isReturnDatePickerOpen,
    setIsReturnDatePickerOpen,
    promoCodeNudgeText,
    setPromoCodeNudgeText,
  };

  return <BookingDataContext.Provider value={values}>{children}</BookingDataContext.Provider>;
}

export default BookingDataContextProvider;
